import React from 'react'
import Box from '@mui/material/Box'
import MuiModal from '@mui/material/Modal'
import IconButton from '@/shared/components/IconButton/IconButton'
import { modalBackgroundStyles, modalStyles } from './Modal.styles'
import styles from './Modal.module.scss'

interface ModalProps {
  children: React.ReactNode
  onClose?: () => void
  size?: 'small' | 'medium' | 'large' | 'fit-content'
  title: string
  isOpen: boolean
  zIndex?: number
  /**
   * Kept on by default, as every existing caller expects. Turn it off for a
   * modal opened from inside another one: the outer modal's Box is centred with
   * a transform, and a transform is a containing block for `position: fixed`
   * descendants, so an in-place nested modal would centre itself inside that
   * box and its backdrop would only cover it.
   */
  disablePortal?: boolean
}

function Modal(props: ModalProps) {
  const {
    children,
    onClose,
    size = 'small',
    title,
    isOpen,
    zIndex,
    disablePortal = true
  } = props

  const handleClose = () => {
    if (onClose) {
      onClose()
    }
  }

  const getWidth = (size: 'small' | 'medium' | 'large' | 'fit-content') => {
    switch (size) {
      case 'small':
        return '400px'
      case 'medium':
        return '640px'
      case 'large':
        return '800px'
      case 'fit-content':
        return 'fit-content'
      default:
        return '600px'
    }
  }

  return (
    <MuiModal
      disablePortal={disablePortal}
      disableEnforceFocus
      disableAutoFocus
      open={isOpen}
      onClose={(_event, reason) => {
        if (reason === 'backdropClick') return
        handleClose()
      }}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
      sx={modalBackgroundStyles}
    >
      <Box sx={{ ...modalStyles, width: getWidth(size), zIndex: zIndex || 6 }}>
        <div className={styles.modalWrapper}>
          <div className={styles.modalHeader}>
            <p className={`title-bold-medium ${styles.modalTitle}`}>{title}</p>
            <IconButton
              color="primary"
              icon="las la-times"
              onClick={handleClose}
            />
          </div>
          {children}
        </div>
      </Box>
    </MuiModal>
  )
}

export default Modal
